/**
 * Конвертирует объект Blob в строку base64.
 *
 * @param {Blob} blob - Объект Blob, который нужно конвертировать.
 * @returns {Promise<string>} - Промис, который разрешается в строку base64.
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

/**
 * Конвертирует строку base64 в объект Blob.
 *
 * @param {string} base64 - Строка в формате base64.
 * @param {string} [mimeType='application/octet-stream'] - MIME-тип для создаваемого Blob.
 * @returns {Blob} - Объект Blob, созданный из строки base64.
 */
export const base64ToBlob = (
  base64: string,
  mimeType: string = 'application/octet-stream'
): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

/**
 * Конвертирует буфер в строку base64.
 *
 * @param {any} buffer - Буфер, который нужно конвертировать.
 * @returns {string} - Строка в формате base64, созданная из буфера.
 */
export const bufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
};

/**
 * Конвертирует файл в строку base64.
 *
 * @param {File} file - Файл, который нужно конвертировать.
 * @returns {Promise<string>} - Промис, который разрешается в строку base64.
 */
export const convertFileToBase64 = async (file: File): Promise<string[]> => {
  try {
    const base64String = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
    return base64String.split(',');
  } catch {
    return ['', ''];
  }
};

/**
 * Функция `calculateFileSizeInMB` вычисляет размер файла в мегабайтах (MB), исходя из его размера в байтах.
 *
 * @param sizeInBytes - Размер файла в байтах, который требуется преобразовать в мегабайты.
 * @returns Строка, представляющая размер файла в мегабайтах с округлением до двух знаков после запятой.
 *
 * @example
 * ```typescript
 * const fileSize = calculateFileSizeInMB(10485760); // "10.00"
 * ```
 */
export const calculateFileSizeInMB = (sizeInBytes: number): string => {
  const sizeInMB = sizeInBytes / (1024 * 1024);
  return sizeInMB.toFixed(2);
};

/**
 * Функция addExtensionIfMissing проверяет, содержит ли название файла расширение.
 * Если расширение отсутствует, функция добавляет его из ссылки на файл и возвращает обновленное имя.
 *
 * @param {Object} file - Объект с информацией о файле.
 * @param {string} file.name - Название файла без расширения или с ним.
 * @param {string} file.url - Ссылка на файл, содержащая расширение.
 *
 * @returns {string} - Название файла с расширением, если оно отсутствовало, или оригинальное название.
 */
export const addExtensionIfMissing = (file: {
  name: string;
  url: string;
}): string => {
  // Регулярное выражение для получения расширения из ссылки (после последней точки)
  const urlExtensionMatch = file.url.match(/\.(\w+)(?:\?|#|$)/);
  const urlExtension = urlExtensionMatch ? urlExtensionMatch[1] : null;

  // Проверка, есть ли расширение в названии файла
  const hasExtension = /\.\w+$/.test(file.name);

  // Если расширение в названии отсутствует и оно найдено в ссылке, добавляем его
  if (!hasExtension && urlExtension) {
    return `${file.name}.${urlExtension}`;
  }

  return file.name;
};

/**
 * Сохраняет файл из data URL через временную ссылку `download` — без сторонних библиотек.
 * Ссылка не попадает в DOM: браузер скачивает по программному клику.
 */
export const downloadDataUrl = (dataUrl: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  link.click();
};

/** Почему файл не подходит для загрузки: неподходящий тип или слишком большой размер. */
export type TUploadFileProblem = 'type' | 'size';

/**
 * Растровые форматы картинок, которые можно загрузить. SVG не принимается: это
 * документ со скриптами, открытый по прямой ссылке, он выполнит их на сайте хранилища.
 */
export const IMAGE_UPLOAD_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif'
];

/**
 * Проверка картинки до чтения: MIME-тип из `IMAGE_UPLOAD_TYPES` и размер не больше
 * `maxSize` байт. Возвращает проблему или `null`, если файл подходит.
 */
export const getImageFileProblem = (
  file: Pick<File, 'type' | 'size'>,
  maxSize: number
): TUploadFileProblem | null => {
  if (!IMAGE_UPLOAD_TYPES.includes(file.type)) return 'type';
  if (file.size > maxSize) return 'size';
  return null;
};

/** MIME-тип файла Excel (.xlsx). */
export const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Сколько ждать перед освобождением ссылки на файл: браузер должен успеть начать скачивание. */
const OBJECT_URL_TTL_MS = 1000;

/** Сохраняет Blob файлом через временную ссылку — например, HTML-отчёт, пришедший строкой. */
export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  downloadDataUrl(url, fileName);
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
};

/**
 * Сохраняет файл из base64 (так бэкенд отдаёт выгрузки отчётов) через временную
 * ссылку на Blob — без сторонних библиотек и без ограничения длины data URL.
 */
export const saveBase64AsFile = (
  base64: string,
  mimeType: string,
  fileName: string
): void => saveBlobAsFile(base64ToBlob(base64, mimeType), fileName);
